/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import { ViewModel } from '../../../core/viewmodel/ViewModel';
import { RequestAccessModel } from '../../../models/components/RequestAccessModel';
import { SnackbarStore } from '../../../stores/application/SnackbarStore';
import { StoreProvider } from '../../../stores/StoreProvider';
import * as H from 'history';
import { RequestAccessService } from '../../../services/RequestAccessService';
import { LocStorageService } from '../../../services/LocStorageService';
import { AuthStore } from '../../../stores/application/AuthStore';
import { LoginStore } from '../../../stores/components/LoginStore';
import { LanguageStore } from '../../../stores/application/LanguageStore';
import { EventStore } from '../../../stores/application/EventStore';

export class RequestAccessFormViewModel extends ViewModel {
	private requestAccessModel: RequestAccessModel = new RequestAccessModel();
	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly loginStore: LoginStore;
	private readonly languageStore: LanguageStore;
	private readonly eventStore: EventStore;
	private readonly changePasswordStore;
	private readonly locStorageService: LocStorageService;
	private readonly requestAccessService: RequestAccessService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.loginStore = storeProvider.loginStore;
		this.languageStore = storeProvider.languageStore;
		this.eventStore = storeProvider.eventStore;
		this.changePasswordStore = storeProvider.changePasswordStore;
		this.requestAccessService = storeProvider.requestAccessService;
		this.locStorageService = storeProvider.locStorageService;
	}

	public getName(): string {
		return this.requestAccessModel.name;
	}

	public setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		this.requestAccessModel.setName(e.target.value);
	}

	public getEMail(): string {
		return this.requestAccessModel.eMail;
	}

	public setEMail(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		this.requestAccessModel.setEMail(e.target.value);
	}

	public getDepartment(): string {
		return this.requestAccessModel.department;
	}

	public setDepartment(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		this.requestAccessModel.setDepartment(e.target.value);
	}

	public getCheckedTos(): boolean {
		return this.requestAccessModel.checkedTos;
	}

	public setCheckedTos(checkedTos: boolean): void {
		this.requestAccessModel.setCheckedTos(checkedTos);
	}

	public acceptTos = (event: React.ChangeEvent<HTMLInputElement>): void => {
		this.setCheckedTos(event.target.checked);
	};

	public disableRequestButton(): boolean {
		return !(
			!this.getCheckedTos() ||
			this.requestAccessModel.name === '' ||
			this.requestAccessModel.eMail === '' ||
			this.requestAccessModel.department === ''
		);
	}

	public requestAccess(name: string, eMail: string, department: string): Promise<void> {
		return this.requestAccessService
			.register(name, eMail, department)
			.then(() => {
				this.snackbarStore.showSuccess('requestAccess.register.successfully_created');
				this.requestAccessModel.setName('');
				this.requestAccessModel.setEMail('');
				this.requestAccessModel.setDepartment('');
				this.requestAccessModel.setCheckedTos(false);
			})
			.catch(() => {
				this.snackbarStore.showError('requestAccess.register.unsuccessfully_created');
			});
	}
}
